import { DataListItem } from '@brighte/form-components';
import { ModifiedApplicationQuote, RelatedReferralWithSummary } from '@graphql/types';
import {
  FINANCE_PURCHASE,
  FORTNIGHTS_IN_A_YEAR,
  MONTHS_IN_A_YEAR,
  PAYMENT_DELAY_DAYS,
  formatDollars,
  productIds,
} from '@utils';
import { fortnightlyRepaymentAmount, getRepaymentDetails } from './helpers';

export const formatDollarsWithoutCents = (amount: number | string | undefined) =>
  formatDollars(amount, {
    hideZeroCents: true,
    zeroValue: '$0.00',
  });

function getFinanceDetails({
  isCashApplication,
  repaymentTerm,
  subsidy,
  quote,
}: {
  isCashApplication: boolean;
  repaymentTerm: string | number;
  subsidy: number;
  quote?: ModifiedApplicationQuote;
}): DataListItem[] {
  if (!quote || !quote?.productConfiguration) return [];

  const { FINANCIAL_PRODUCT_FIELDS, CASH_FIELDS, NO_EARLY_REPAYMENT_FEES } = FINANCE_PURCHASE;

  const {
    financialProduct: { id },
    productConfiguration,
    totalPurchaseAmount,
    depositAmount,
    rebate = 0,
    relatedReferrals,
  } = quote;

  const {
    establishmentFee,
    weeklyAccountFee,
    latePaymentFee,
    introducerFee,
    interestRate,
    riskBasedPricing,
    minEstablishmentFee,
    maxEstablishmentFee,
  } = productConfiguration ?? {
    establishmentFee: 0,
    minEstablishmentFee: 0,
    maxEstablishmentFee: 0,
    weeklyAccountFee: 0,
    latePaymentFee: 0,
    interestRate: 0,
    riskBasedPricing: false,
  };

  const repayment = getRepaymentDetails(repaymentTerm, quote);

  if (relatedReferrals?.length) {
    const relatedReferralTotalPayments = relatedReferrals.reduce((acc, q) => {
      const repaymentTotalPayments =
        getRepaymentDetails(repaymentTerm, q as RelatedReferralWithSummary)?.totalPayments ?? 0;
      return acc + repaymentTotalPayments;
    }, 0);
    repayment?.totalPayments && (repayment.totalPayments += relatedReferralTotalPayments);
  }

  const promoDelayDays = PAYMENT_DELAY_DAYS(quote);
  const allCategoryQuotes = relatedReferrals?.length ? [quote, ...relatedReferrals] : [quote];
  const consolidatedPurchaseAmount = allCategoryQuotes.reduce((acc, { totalPurchaseAmount }) => {
    return acc + totalPurchaseAmount;
  }, 0);
  const consolidatedDepositAmount = allCategoryQuotes.reduce((acc, { depositAmount }) => {
    return acc + depositAmount;
  }, 0);
  const totalRemainingAmount = consolidatedPurchaseAmount - consolidatedDepositAmount;
  const repaymentsInMonths = Math.floor(Number(repaymentTerm || 0) * (FORTNIGHTS_IN_A_YEAR / MONTHS_IN_A_YEAR));
  const repaymentAmount = fortnightlyRepaymentAmount({
    repayments: repaymentsInMonths,
    total: totalPurchaseAmount - depositAmount,
    accountKeepingFee: productConfiguration?.weeklyAccountFee,
  });

  const COMMON_FIELDS = {
    interestRate: {
      label: riskBasedPricing
        ? FINANCIAL_PRODUCT_FIELDS.interestRateRiskBasePricing
        : FINANCIAL_PRODUCT_FIELDS.interestRate,
      value: `${repayment?.interestRate ?? interestRate}% p.a`,
    },
    sumOfTotalPurchaseAmount: {
      label: CASH_FIELDS.totalPurchaseAmount,
      value: formatDollarsWithoutCents(consolidatedPurchaseAmount),
    },
    sumOfDeposit: {
      label: CASH_FIELDS.depositAmount,
      value: `${depositAmount > 1 ? '-' : ''}${formatDollarsWithoutCents(consolidatedDepositAmount)}`,
    },
    remainingAmount: {
      label: CASH_FIELDS.remainingAmount,
      value: formatDollarsWithoutCents(totalRemainingAmount - subsidy),
    },
    sumOfFinancedAmount: {
      label: FINANCIAL_PRODUCT_FIELDS.financedAmount,
      value: formatDollarsWithoutCents(consolidatedPurchaseAmount - consolidatedDepositAmount - subsidy),
    },
    totalPurchaseAmount: {
      label: CASH_FIELDS.totalPurchaseAmount,
      value: formatDollarsWithoutCents(totalPurchaseAmount),
    },
    depositAmount: {
      label: CASH_FIELDS.depositAmount,
      value: `${depositAmount > 1 ? '-' : ''}${formatDollarsWithoutCents(depositAmount)}`,
    },
    financedAmountWithSubsidy: {
      label: FINANCIAL_PRODUCT_FIELDS.financedAmount,
      value: formatDollarsWithoutCents(totalPurchaseAmount - depositAmount - subsidy),
    },
    financedAmountWithRebate: {
      label: FINANCIAL_PRODUCT_FIELDS.financedAmount,
      value: formatDollarsWithoutCents(totalPurchaseAmount - depositAmount - (rebate ?? 0)),
    },
    subsidy: {
      label: CASH_FIELDS.subsidy,
      value: `${(subsidy ?? 0 > 1) ? '-' : ''}${formatDollarsWithoutCents(subsidy ?? 0)}`,
    },
    rebate: {
      label: FINANCIAL_PRODUCT_FIELDS.rebate,
      value: rebate ? `${rebate > 1 ? '-' : ''}${formatDollarsWithoutCents(rebate)}` : 0,
    },
    totalPayments: {
      label: FINANCIAL_PRODUCT_FIELDS.totalMinimumAmountPayable,
      value: formatDollarsWithoutCents(repayment?.totalPayments ? repayment?.totalPayments : 0),
    },
    establishmentFee: {
      label: FINANCIAL_PRODUCT_FIELDS.establishmentFee,
      value: formatDollarsWithoutCents(establishmentFee),
    },
    minMaxEstablishmentFee: {
      label: FINANCIAL_PRODUCT_FIELDS.establishmentFee,
      value: `${formatDollarsWithoutCents(minEstablishmentFee)} - ${formatDollarsWithoutCents(maxEstablishmentFee)}`,
    },
    weeklyAccountFee: {
      label: FINANCIAL_PRODUCT_FIELDS.weeklyAccountFee,
      value: formatDollars(weeklyAccountFee),
    },
    weeklyAccountFeeMultiCategory: {
      label: FINANCIAL_PRODUCT_FIELDS.weeklyAccountFeeMultiCategory,
      value: formatDollars(weeklyAccountFee),
    },
    introducerFee: {
      label: FINANCIAL_PRODUCT_FIELDS.introducerFee,
      value: formatDollarsWithoutCents(introducerFee),
    },
    latePaymentFee: {
      label: FINANCIAL_PRODUCT_FIELDS.latePaymentFee,
      value: formatDollars(latePaymentFee),
    },
    latePaymentFeeMultiCategory: {
      label: FINANCIAL_PRODUCT_FIELDS.latePaymentFeeMultiCategory,
      value: formatDollars(latePaymentFee),
    },
    noEarlyRepayments: {
      label: NO_EARLY_REPAYMENT_FEES,
    },
    firstRepaymentDates: {
      label: `First repayment due ${promoDelayDays} days after installation`,
    },
    loanTerm: {
      label: FINANCIAL_PRODUCT_FIELDS.loanTerm,
      value: repayment?.termInMonths ? `${repayment?.termInMonths} months` : 0,
    },
    amountOnFirstRepaymentDate: {
      label: FINANCIAL_PRODUCT_FIELDS.amountOnFirstRepaymentDate,
      value: formatDollars(Number(repaymentAmount) + establishmentFee),
    },
    fortnightlyRepaymentFee: {
      label: 'Fortnightly repayments',
      value: formatDollars(repaymentAmount || 0),
    },
    financedAmount: {
      label: FINANCIAL_PRODUCT_FIELDS.financedAmountExact,
      value: formatDollarsWithoutCents(totalPurchaseAmount - depositAmount),
    },
  };

  switch (id) {
    case productIds.ELECTRIFY_2515: {
      if (isCashApplication) {
        return [
          COMMON_FIELDS.sumOfTotalPurchaseAmount,
          COMMON_FIELDS.sumOfDeposit,
          ...(subsidy && subsidy > 0 ? [COMMON_FIELDS.subsidy] : []),
          COMMON_FIELDS.remainingAmount,
        ];
      } else {
        return [
          {
            symbol: '1',
            ...COMMON_FIELDS.interestRate,
          },
          COMMON_FIELDS.sumOfTotalPurchaseAmount,
          COMMON_FIELDS.sumOfDeposit,
          ...(subsidy && subsidy > 0 ? [COMMON_FIELDS.subsidy] : []),
          { symbol: '2', ...COMMON_FIELDS.sumOfFinancedAmount },
          { symbol: '3', ...COMMON_FIELDS.totalPayments },
          { symbol: '4', ...COMMON_FIELDS.minMaxEstablishmentFee },
          COMMON_FIELDS.weeklyAccountFeeMultiCategory,
          ...(introducerFee ? [COMMON_FIELDS.introducerFee] : []),
          COMMON_FIELDS.latePaymentFeeMultiCategory,
          COMMON_FIELDS.noEarlyRepayments,
          COMMON_FIELDS.firstRepaymentDates,
        ];
      }
    }
    case productIds.BGL:
    case productIds.BPL:
    case productIds.HEUF_LOAN: {
      if (riskBasedPricing) {
        return [
          {
            symbol: '1',
            ...COMMON_FIELDS.interestRate,
          },
          COMMON_FIELDS.totalPurchaseAmount,
          COMMON_FIELDS.depositAmount,
          ...(subsidy && subsidy > 0 ? [COMMON_FIELDS.subsidy] : []),
          COMMON_FIELDS.financedAmountWithSubsidy,
          {
            symbol: '2',
            ...COMMON_FIELDS.totalPayments,
          },
          COMMON_FIELDS.establishmentFee,
          COMMON_FIELDS.weeklyAccountFee,
          ...(introducerFee ? [COMMON_FIELDS.introducerFee] : []),
          COMMON_FIELDS.latePaymentFee,
          COMMON_FIELDS.noEarlyRepayments,
          COMMON_FIELDS.firstRepaymentDates,
        ];
      }
      return [
        COMMON_FIELDS.interestRate,
        COMMON_FIELDS.totalPurchaseAmount,
        COMMON_FIELDS.depositAmount,
        ...(subsidy && subsidy > 0 ? [COMMON_FIELDS.subsidy] : []),
        COMMON_FIELDS.financedAmountWithSubsidy,
        {
          symbol: '1',
          ...COMMON_FIELDS.totalPayments,
        },
        COMMON_FIELDS.establishmentFee,
        COMMON_FIELDS.weeklyAccountFee,
        ...(introducerFee ? [COMMON_FIELDS.introducerFee] : []),
        COMMON_FIELDS.latePaymentFee,
        COMMON_FIELDS.noEarlyRepayments,
        COMMON_FIELDS.firstRepaymentDates,
      ];
    }
    case productIds.FLEX: {
      return [
        COMMON_FIELDS.totalPurchaseAmount,
        COMMON_FIELDS.depositAmount,
        ...(rebate && rebate > 0 ? [COMMON_FIELDS.rebate] : []),
        COMMON_FIELDS.financedAmountWithRebate,
        COMMON_FIELDS.totalPayments,
        COMMON_FIELDS.establishmentFee,
        COMMON_FIELDS.weeklyAccountFee,
        ...(introducerFee ? [COMMON_FIELDS.introducerFee] : []),
        {
          symbol: '1',
          ...COMMON_FIELDS.latePaymentFee,
        },
        COMMON_FIELDS.noEarlyRepayments,
        {
          label: `Single payment of ${formatDollarsWithoutCents(quote.financedAmount)} due in ${promoDelayDays} days after installation`,
        },
      ];
    }
    case productIds.BRIGHTE_PAY: {
      return [
        COMMON_FIELDS.totalPurchaseAmount,
        { ...COMMON_FIELDS.depositAmount, label: FINANCIAL_PRODUCT_FIELDS.depositAmountExact },
        COMMON_FIELDS.financedAmount,
        COMMON_FIELDS.loanTerm,
        COMMON_FIELDS.amountOnFirstRepaymentDate,
        {
          label: `(Includes first fortnightly payment plus the ${formatDollarsWithoutCents(establishmentFee)} establishment fee)`,
        },
        {
          symbol: '1',
          ...COMMON_FIELDS.fortnightlyRepaymentFee,
        },
      ];
    }
    case productIds.ACT_SHS: {
      return [
        COMMON_FIELDS.interestRate,
        COMMON_FIELDS.totalPurchaseAmount,
        COMMON_FIELDS.depositAmount,
        ...(rebate && rebate > 0 ? [COMMON_FIELDS.rebate] : []),
        COMMON_FIELDS.financedAmountWithRebate,
        {
          symbol: '1',
          ...COMMON_FIELDS.totalPayments,
        },
        COMMON_FIELDS.establishmentFee,
        COMMON_FIELDS.weeklyAccountFee,
        ...(introducerFee ? [COMMON_FIELDS.introducerFee] : []),
        COMMON_FIELDS.latePaymentFee,
        COMMON_FIELDS.noEarlyRepayments,
        COMMON_FIELDS.firstRepaymentDates,
      ];
    }
    default: {
      return [
        COMMON_FIELDS.totalPurchaseAmount,
        COMMON_FIELDS.depositAmount,
        ...(rebate && rebate > 0 ? [COMMON_FIELDS.rebate] : []),
        COMMON_FIELDS.financedAmountWithRebate,
        COMMON_FIELDS.totalPayments,
        COMMON_FIELDS.establishmentFee,
        COMMON_FIELDS.weeklyAccountFee,
        ...(introducerFee ? [COMMON_FIELDS.introducerFee] : []),
        {
          symbol: '1',
          ...COMMON_FIELDS.latePaymentFee,
        },
        COMMON_FIELDS.noEarlyRepayments,
        COMMON_FIELDS.firstRepaymentDates,
      ];
    }
  }
}

export default getFinanceDetails;
